import React, { createContext, useState, useContext, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
// Assuming you have a way to get 'location', e.g., from react-router-dom
// import { useLocation } from 'react-router-dom';


export type TextData = {
  text: string;
  variant: "danger" | "warning" | "" | "success" | "info";
};
export type SetTextData = (textData: TextData) => void;

const InitialMessageType: TextData = { text: "", variant: "" };

// Create a context with a default value
const MessageContext = createContext<{
  textData: TextData;
  setTextData: SetTextData;
}>({
  textData: InitialMessageType,
  setTextData: () => {} // No-op function for default
});


export const MessageProvider = ({ children }: { children: React.ReactNode }) => {
  const [textData, setTextData] = useState<TextData>(InitialMessageType);

  return (
    <MessageContext.Provider value={{ textData, setTextData }}>
      {children}
    </MessageContext.Provider>
  );
};
export const useMessage = () => useContext(MessageContext);


export const MessageDisplay = ({ delay = 9000 }: { delay?: number }) => {
  const [show, setShow] = useState(false);
  const { textData, setTextData } = useMessage();


  // Effect to show/hide message based on textData
  useEffect(() => {
    if (textData && textData.text !== "") {
      setShow(true);
      const timer = setTimeout(() => {
        setTextData({ text: "", variant: "" });
        setShow(false);
      }, delay);
      return () => clearTimeout(timer);
    } else {
      setShow(false);
    }
  }, [textData, delay, setTextData]);

  const handleAlertClose = () => {
    setTextData({ text: "", variant: "" });
    setShow(false);
  };

  const variantClasses: Record<TextData['variant'], string> = {
    "danger": "bg-red-500",
    "warning": "bg-yellow-500",
    "success": "bg-green-500",
    "": "bg-gray-700",
    "info": "bg-blue-500" // Added info variant
  };

  const backgroundClass = variantClasses[textData.variant] || variantClasses[""];

  if (!show) {
    return null;
  }

  return (
    // Outer container for positioning at the bottom
    <div className="fixed mb-10 bottom-0 left-0 right-0  p-4 pointer-events-none" style={{ zIndex: 9999 }}>
      {/* Inner container for the message content */}
      <div className={`
          mx-auto w-full max-w-md md:max-w-lg rounded-lg shadow-lg p-4 text-white flex items-start justify-between space-x-4 pointer-events-auto
          ${backgroundClass}
          ${textData.variant === 'warning' ? 'text-gray-800' : ''}
          max-h-32 overflow-hidden /* Added: Set max height and hide overflow initially */
        `}>
        {/* Message text container with overflow handling and line clamping */}
        {/* For line clamping, you might need the @tailwindcss/line-clamp plugin */}
        {/* If using the plugin, you could use: className="flex-grow break-words line-clamp-3" */}
        {/* Without the plugin, we can use a combination of max-height on the parent and overflow-hidden on the text, or allow scrolling */}
        <div className="flex-grow overflow-y-auto max-h-[calc(8rem-2rem)]"> {/* 8rem is max-h-32, 2rem is p-4 vertical padding */}
            <p className="break-words"> {/* break-words helps prevent long strings from breaking layout */}
                {textData.text}
            </p>
        </div>

        {/* Close button */}
        <button
          className="text-white opacity-90 hover:opacity-100  flex-shrink-0 ml-2" // Added flex-shrink-0 and ml-2 for spacing
          onClick={handleAlertClose}
          aria-label="Close message"
        >
          <FaTimes className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
